import { PlusOutlined, QrcodeOutlined } from "@ant-design/icons";
import { Button, message, Space, Tooltip } from "antd";
import _ from "lodash";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../api";
import apiPaths from "../../api/paths";
import DynamicTable from "../../components/DynamicTable";
import VehicleModal from "./components/VehicleModal";
import { createFileName, useScreenshot } from "use-react-screenshot";
import QRCode from "react-qr-code";
import CryptoJS from "crypto-js";
import images from "../../assets/images";

const DEFAULT_COLUMNS = {
  name: {
    show: true,
    label: "Vehicle Name",
    key: "name",
  },
  code: {
    show: true,
    label: "Code",
    key: "code",
  },
  "vehicle_type.title": {
    show: true,
    label: "Type",
    key: "vehicle_type.title",
  },
  ride_charges: {
    show: true,
    label: "Ride Charges",
    key: "ride_charges",
  },
  "dock.name": {
    show: true,
    label: "Dock Station",
    key: "dock.name",
  },
  date_create: {
    show: false,
    label: "Created Date",
    key: "date_create",
  },
  date_modify: {
    show: false,
    label: "Updated Date",
    key: "date_modify",
  },
};

function Vehicles() {
  const [editingKey, setEditingKey] = useState(null);
  const [vehicles, setVehicles] = useState({});
  const [stations, setStations] = useState([]);
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [image, takeScreenshot] = useScreenshot();
  const ref = useRef(null);

  const download = (image, { name = "img", extension = "png" } = {}) => {
    const a = document.createElement("a");
    a.href = image;
    a.download = createFileName(extension, name);
    a.click();
  };

  useEffect(() => {
    fetchVehicles();
    fetchStations();
  }, []);

  const fetchStations = async () => {
    const res = await apiRequest({
      path: apiPaths.stations,
    });
    if (!res.error) {
      setStations(res.results);
    }
  };

  const fetchVehicles = async () => {
    const res = await apiRequest({
      path: apiPaths.vehicles,
    });
    if (!res.error) {
      let vehiclesObj = res.results.reduce((vehicles, vehicle) => {
        vehicles[vehicle.id + "v"] = vehicle;
        return vehicles;
      }, {});
      setVehicles(vehiclesObj);
    } else {
      message.error(res.error_description);
    }
  };

  const getImage = (id) => {
    return takeScreenshot(ref.current).then((img) =>
      download(img, { name: "bike_" + id, extension: "png" })
    );
  };

  const handleGenerateQRCode = (data) => {
    const TYPES = {
      "Ingo Bike": "bike",
      Barc: "auto",
      Bike: "bike",
    };
    let code = CryptoJS.MD5(`code#@#@${data.code}`).toString();
    let id = CryptoJS.MD5(`id#@#@${data.id}`).toString();
    setQrCodeData({
      codeData: { code, id, type: TYPES[data.vehicle_type?.title] },
      data,
    });
    setTimeout(() => {
      getImage(data.name + "_" + data.code);
    }, 1000);
  };

  const COLUMNS = [
    {
      title: "Vehicle Name",
      dataIndex: "name",
      fixed: "left",
      width: 150,
      filterType: "search",
    },
    {
      title: "Code",
      dataIndex: "code",
      width: 150,
      filterType: "search",
    },
    {
      title: "Type",
      dataIndex: "vehicle_type.title",
      filterType: "search",
      width: 70,
      render: (_, record) => {
        return record.vehicle_type?.title;
      },
    },
    {
      title: "Ride Charges",
      dataIndex: "ride_charges",
      width: 100,
    },
    {
      title: "Dock Station",
      dataIndex: "dock.name",
      filterType: "search",
      width: "150px",
      render: (_, record) => {
        return (
          <div>
            <div>{record.dock?.name || "NA"}</div>
          </div>
        );
      },
    },
    {
      title: "Created Date",
      dataIndex: "date_create",
      width: "150px",
      render: (value) => {
        return <div>{value && moment(value).format("DD-MM-YYYY HH:mm")}</div>;
      },
      filterType: "date",
    },

    {
      title: "Updated Date",
      dataIndex: "date_modify",
      filterType: "date",
      width: "150px",
      render: (value) => {
        return <div>{value && moment(value).format("DD-MM-YYYY HH:mm")}</div>;
      },
    },
    {
      title: "Qr Code",
      dataIndex: "qrcode",
      width: "50px",
      render: (text, record) => (
        <>
          {["Ingo Bike", "Bike", "Barc"].includes(
            record.vehicle_type?.title
          ) ? (
            <Tooltip title="download QR code">
              <Button
                onClick={() => handleGenerateQRCode(record)}
                type="link"
                style={{ marginLeft: 10 }}
              >
                <QrcodeOutlined style={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          ) : null}
        </>
      ),
    },
  ];

  const handleRowCanceled = () => {
    setShowVehicleModal(false);
  };

  const handleRowEdit = (idx) => {
    setEditingKey(idx);
    setShowVehicleModal(true);
  };

  const handleRowSave = async (row) => {
    if (editingKey == null) {
      const res = await apiRequest({
        path: apiPaths.vehicles,
        method: "POST",
        data: row,
      });
      if (res.error) {
        message.error(res.error_description);
        return;
      }
      setVehicles({
        [res.id]: res,
        ...vehicles,
      });
      setShowVehicleModal(false);
      return;
    }
    let key = editingKey;
    const res = await apiRequest({
      path: apiPaths.vehicles + `/${key}`,
      method: "PUT",
      data: row,
    });
    if (res.error) {
      message.error(res.error_description);
      return;
    }
    vehicles[editingKey + "v"] = res;
    setVehicles({ ...vehicles });
    setEditingKey(null);
    setShowVehicleModal(false);
  };

  return (
    <div style={{ height: "80vh" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginRight: 10,
        }}
      >
        <Space>
          <Tooltip title="Add Vehicle">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingKey(null);
                setShowVehicleModal(true);
              }}
            />
          </Tooltip>
        </Space>
      </div>
      <div style={{ height: "90%", marginTop: 30 }}>
        <DynamicTable
          dataSource={Object.values(vehicles)}
          columns={COLUMNS}
          defaultColumns={DEFAULT_COLUMNS}
          pagination={false}
          scroll={{ y: "65vh" }}
          editHandler={(record) => handleRowEdit(record.id)}
        />
      </div>
      {qrCodeData ? (
        <div
          style={{
            position: "absolute",
            right: -500,
            backgroundColor: "#fff",
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: 450,
            height: 600,
          }}
          ref={(_ref) => (ref.current = _ref)}
        >
          <img
            src={images.qrCodeFrameImg}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
          <div
            style={{
              fontSize: 10,
              fontWeight: "bold",
              position: "absolute",
              color: "#fff",
              top: "8%",
              left: "8%",
            }}
          >
            {qrCodeData.data.name}
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: "bold",
              position: "absolute",
              color: "#fff",
              bottom: 10,
            }}
          >
            {`SCAN THIS CODE TO ${
              qrCodeData.codeData.type == "bike" ? "RIDE" : "PAY"
            }.`}
          </div>
          <div
            style={{
              height: "60%",
              width: "75%",
              position: "absolute",
              bottom: "10%",
            }}
          >
            <QRCode
              style={{ width: "100%", height: "100%" }}
              value={JSON.stringify(qrCodeData.codeData)}
            />
          </div>
        </div>
      ) : null}
      {showVehicleModal ? (
        <VehicleModal
          open={showVehicleModal}
          onSave={handleRowSave}
          onCancel={handleRowCanceled}
          data={vehicles[editingKey + "v"]}
          isUpdate={editingKey != null}
          stations={stations}
        />
      ) : null}
    </div>
  );
}

export default Vehicles;
